import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Landmark, BellRing, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { financeAPI } from '../lib/api';

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (d) => new Date(d + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });

const ConsentStatus = ({ st }) => {
  if (!st?.configured) return <p className="text-sm text-muted-foreground" data-testid="bank-consent-empty">Indica cuándo conectaste el banco por última vez en Fintable.</p>;
  const tone = st.expired ? 'text-red-700 bg-red-50 border-red-200' : st.warning ? 'text-amber-800 bg-amber-50 border-amber-300' : 'text-emerald-800 bg-emerald-50 border-emerald-200';
  const Icon = st.warning ? AlertTriangle : CheckCircle2;
  return (
    <div className={`flex gap-2 items-start rounded-lg border p-3 text-sm ${tone}`} data-testid="bank-consent-status">
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-semibold" data-testid="bank-consent-days-left">
          {st.expired ? `Caducó hace ${-st.days_left} días` : `Quedan ${st.days_left} días`}
        </p>
        <p>Conectado el {fmt(st.consent_date)} · caduca el {fmt(st.expires_on)}</p>
        {st.warning && <p className="mt-1">Reconecta el banco en Fintable y pulsa «He reconectado hoy».</p>}
      </div>
    </div>
  );
};

export const BankConsentCard = () => {
  const [st, setSt] = useState(null);
  const [date, setDate] = useState(today());
  const [days, setDays] = useState(90);

  useEffect(() => {
    financeAPI.getBankConsent().then((r) => { setSt(r); if (r.consent_date) setDate(r.consent_date); setDays(r.valid_days || 90); }).catch(() => {});
  }, []);

  const save = async (d) => {
    try {
      const r = await financeAPI.setBankConsent(d, days);
      setSt(r); setDate(r.consent_date);
      toast.success('Fecha de conexión del banco guardada');
    } catch (e) { toast.error(e.message || 'No se pudo guardar'); }
  };

  return (
    <Card data-testid="bank-consent-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><Landmark className="w-5 h-5 text-primary" />Caducidad del banco (PSD2)</CardTitle>
        <CardDescription>Aviso a Marta 7 días antes de que caduque la conexión con Fintable.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ConsentStatus st={st} />
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="bank-consent-date-input" />
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-9 rounded-md border px-2 text-sm bg-background" data-testid="bank-consent-valid-days">
            <option value={90}>Válido 90 días</option>
            <option value={180}>Válido 180 días</option>
          </select>
          <Button variant="outline" size="sm" onClick={() => save(date)} data-testid="bank-consent-save-btn">Guardar fecha</Button>
          <Button size="sm" onClick={() => save(today())} data-testid="bank-consent-today-btn">He reconectado hoy</Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const FinanceWebhookCard = () => {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    financeAPI.getDiscordWebhook().then((r) => setUrl(r.finance_discord_webhook_url || '')).catch(() => {});
  }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); } catch (e) { toast.error(e.message || 'Error'); } finally { setBusy(false); }
  };

  return (
    <Card data-testid="finance-webhook-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base"><BellRing className="w-5 h-5 text-primary" />Avisos financieros a Marta</CardTitle>
        <CardDescription>Cargos inesperados, resumen mensual y caducidad del banco. Llegan al portal de Marta y a este Discord.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input placeholder="https://discord.com/api/webhooks/..." value={url} onChange={(e) => setUrl(e.target.value)} data-testid="finance-webhook-input" />
        <div className="flex gap-2">
          <Button size="sm" disabled={busy} onClick={() => run(() => financeAPI.setDiscordWebhook(url.trim()), 'Webhook guardado')} data-testid="finance-webhook-save-btn">Guardar</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => financeAPI.testDiscordWebhook(), 'Mensaje de prueba enviado a Discord')} data-testid="finance-webhook-test-btn">Enviar prueba</Button>
        </div>
      </CardContent>
    </Card>
  );
};
